import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, XCircle, Clock, Loader2, MessageSquare, Mic, FileText, Mail, Linkedin } from "lucide-react";

// --- UNIFIED STATUS MAP ---
// A single source for statuses with icons, text, and classNames for a cohesive look.
const statusMap = {
  1: { text: "Not Started", Icon: Clock, className: "text-slate-400 border-slate-700" },
  2: { text: "Waiting", Icon: Loader2, className: "text-blue-400 border-blue-800 animate-spin" },
  3: { text: "Reviewed", Icon: AlertCircle, className: "text-yellow-400 border-yellow-800" },
  4: { text: "Sent", Icon: CheckCircle2, className: "text-green-400 border-green-800" },
  5: { text: "Opened", Icon: AlertCircle, className: "text-yellow-400 border-yellow-800" },
  6: { text: "Replied", Icon: CheckCircle2, className: "text-teal-400 border-teal-800" },
  7: { text: "Generating", Icon: Loader2, className: "text-purple-400 border-purple-800 animate-spin" },
  8: { text: "Generate Failed", Icon: XCircle, className: "text-red-400 border-red-800" },
  9: { text: "Sending", Icon: Loader2, className: "text-blue-400 border-blue-800 animate-spin" },
  10: { text: "Send Failed", Icon: XCircle, className: "text-red-400 border-red-800" },
};

// --- UNIFIED PROGRESS STAGES ---
const progressStages = [
  { id: 0, text: "Email 1", channel: "email", color: "from-fuchsia-500 to-purple-500" },
  { id: 1, text: "LinkedIn 1", channel: "linkedin", color: "from-blue-500 to-cyan-400" },
  { id: 2, text: "Email 2", channel: "email", color: "from-lime-500 to-green-500" },
  { id: 3, text: "LinkedIn 2", channel: "linkedin", color: "from-orange-500 to-red-500" },
  { id: 4, text: "Email 3", channel: "email", color: "from-pink-500 to-rose-500" },
  { id: 5, text: "LinkedIn 3", channel: "linkedin", color: "from-indigo-500 to-violet-500" },
  { id: 6, text: "Email 4", channel: "email", color: "from-teal-400 to-emerald-500" },
  { id: 7, text: "LinkedIn 4", channel: "linkedin", color: "from-red-500 to-rose-500" },
];

// --- ICON HELPERS (LUCIDE) ---
const channelIcons = {
  email: Mail,
  linkedin: Linkedin,
};
const typeIcons = {
  text: { Icon: MessageSquare, text: "Text" },
  voice: { Icon: Mic, text: "Voice" },
  proposal: { Icon: FileText, text: "Proposal" },
};

// --- StatusBadge ---
const StatusBadge = ({ status }) => {
  const data = statusMap[status] || statusMap[1];
  return (
    <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-900/50 border ${data.className}`}>
      <data.Icon className="h-3.5 w-3.5" />
      {data.text}
    </span>
  );
};

// --- TimelineItem ---
const TimelineItem = ({ step, index }) => {
  const stage = progressStages.find(s => s.id === step.id);
  const typeData = typeIcons[step.type];
  const isEven = index % 2 === 0;
  const ChannelIcon = channelIcons[stage.channel];

  const itemVariants = {
    hidden: { opacity: 0, x: isEven ? -50 : 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } }
  };

  return (
    <motion.div
      className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group"
      variants={itemVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.5 }}
    >
      {/* Central Icon with Channel-specific icon */}
      <div className={`flex items-center justify-center w-12 h-12 rounded-full bg-slate-800 border-2 border-slate-700 md:absolute md:left-1/2 md:-translate-x-1/2 z-10 bg-gradient-to-br ${stage.color} shadow-[0_0_20px_-5px] ${stage.color.split(' ')[0].replace('from-','')+'500'}`}>
        <ChannelIcon className="w-6 h-6 text-white" />
      </div>

      {/* Content Card with Watermark */}
      <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)]">
        <div className="relative p-4 rounded-xl border border-white/10 bg-slate-800/50 backdrop-blur-lg shadow-lg transition-all duration-300 group-hover:border-white/20 group-hover:bg-slate-800/80 overflow-hidden">
          
          <span className="absolute -right-2 -bottom-4 text-8xl font-black text-white/5 select-none pointer-events-none">
            {stage.id + 1}
          </span>
          
          <div className="relative z-10">
            <h5 className="text-sm font-bold text-white mb-3">{stage.text}</h5>
            <div className="flex items-center justify-between gap-4">
              <StatusBadge status={step.status} />
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <typeData.Icon className="w-3.5 h-3.5" />
                <span>{typeData.text}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// --- Timeline ---
const Timeline = ({ steps = [] }) => {
  return (
    <div className="relative w-full space-y-8">
      {/* Central Connecting Line */}
      <div className="hidden md:block absolute top-0 left-1/2 w-0.5 h-full -translate-x-1/2 bg-gradient-to-b from-transparent via-cyan-500 to-transparent" />
      {steps.map((step, index) => (
        <TimelineItem key={step.id} step={step} index={index} />
      ))}
    </div>
  );
};


// --- CampaignPopup ---
const CampaignPopup = ({ steps = [] }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  // Generate demo steps if none are provided
  const demoSteps = progressStages.map((stage) => {
    const types = ["text", "voice", "proposal"];
    return {
      id: stage.id,
      channel: stage.channel,
      type: types[Math.floor(Math.random() * types.length)],
      status: Math.floor(Math.random() * 10) + 1,
    };
  });
  const finalSteps = steps.length ? steps : demoSteps;

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const popupHeightEstimate = window.innerHeight * 0.7; // Corresponds to max-h-[70vh]

      // Open upwards if there's insufficient space below but more space above
      setOpenUp(spaceBelow < popupHeightEstimate && rect.top > spaceBelow);
    }
    setOpen(!open);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative inline-block font-sans">
      {/* Style tag for custom scrollbar */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(100, 116, 139, 0.5);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(100, 116, 139, 0.8);
        }
      `}</style>

      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-slate-800 border border-slate-700 hover:bg-slate-700/50 transition-all duration-300 shadow-lg hover:shadow-cyan-500/20"
      >
        View Cosmic Flow
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={popupRef}
            initial={{ opacity: 0, y: openUp ? 20 : -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: openUp ? 20 : -20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={`custom-scrollbar absolute ${ openUp ? 'bottom-full mb-3' : 'top-full mt-3' } left-1/2 -translate-x-1/2 z-50 w-[90vw] max-w-lg bg-slate-900/80 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl p-6 max-h-[70vh] overflow-y-auto overflow-x-hidden`}
          >
            <h4 className="text-xl font-bold text-white mb-6 text-center">
              Outreach Campaign Timeline
            </h4>
            <Timeline steps={finalSteps.sort((a,b) => a.id - b.id)} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CampaignPopup;

